package it.neckar.lizergy.model.http

import it.neckar.lizergy.model.price.PriceListExportCategory
import it.neckar.open.annotations.Effective
import it.neckar.open.http.Url
import it.neckar.open.http.UrlPattern
import kotlin.uuid.Uuid

/**
 * Contains constants for the Planner API.
 *
 * This class does *not* contain the hostname. Just the path.
 *
 * Execute `PlannerEndpointsEffectiveTest` to update the @Effective annotations
 */
object PlannerEndpoints {

  @Effective("api")
  private val BasePath: UrlPattern.Relative0 = Url.pattern("api")

  //TODO: Move the methods to URL support. This object should just contain constants

  /**
   * Authentication endpoints
   */
  object Auth : Endpoints {
    @Effective("api/auth")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "auth"

    @Effective("api/auth/login")
    val Login: UrlPattern.Relative0 = baseUrl + "login"
    fun loginUrl(): Url.Relative = Login.resolve()

    @Effective("api/auth/refresh-access-token")
    val RefreshAccessToken: UrlPattern.Relative0 = baseUrl + "refresh-access-token"
    fun refreshAccessTokenUrl(): Url.Relative = RefreshAccessToken.resolve()

    @Effective("api/auth/verify")
    val Verify: UrlPattern.Relative0 = baseUrl + "verify"
    fun verifyUrl(): Url.Relative = Verify.resolve()

    @Effective("api/auth/verify/access-token")
    val VerifyAccessToken: UrlPattern.Relative0 = Verify + "access-token"
    fun verifyAccessTokenUrl(): Url.Relative = VerifyAccessToken.resolve()

    @Effective("api/auth/verify/refresh-token")
    val VerifyRefreshToken: UrlPattern.Relative0 = Verify + "refresh-token"
    fun verifyRefreshTokenUrl(): Url.Relative = VerifyRefreshToken.resolve()

    @Effective("api/auth/changePassword")
    val ChangePassword: UrlPattern.Relative0 = baseUrl + "changePassword"
    fun changePasswordUrl(): Url.Relative = ChangePassword.resolve()

    @Effective("api/auth/add-new-user")
    val AddNewUser: UrlPattern.Relative0 = baseUrl + "add-new-user"
    fun addNewUserUrl(): Url.Relative = AddNewUser.resolve()

    @Effective("api/auth/change-user-info")
    val ChangeUserInfo: UrlPattern.Relative0 = baseUrl + "change-user-info"
    fun changeUserInfoUrl(): Url.Relative = ChangeUserInfo.resolve()

    @Effective("api/auth/delete-user")
    val DeleteUser: UrlPattern.Relative0 = baseUrl + "delete-user"
    fun deleteUserUrl(): Url.Relative = DeleteUser.resolve()

    @Effective("api/auth/add-new-company")
    val AddNewCompany: UrlPattern.Relative0 = baseUrl + "add-new-company"
    fun addNewCompanyUrl(): Url.Relative = AddNewCompany.resolve()

    @Effective("api/auth/change-company-info")
    val ChangeCompanyInfo: UrlPattern.Relative0 = baseUrl + "change-company-info"
    fun changeCompanyInfoUrl(): Url.Relative = ChangeCompanyInfo.resolve()

    @Effective("api/auth/list-users-and-companies")
    val ListUsersAndCompanies: UrlPattern.Relative0 = baseUrl + "list-users-and-companies"
    fun listUsersAndCompaniesUrl(): Url.Relative = ListUsersAndCompanies.resolve()
  }

  /**
   * Geo information endpoints
   */
  object GeoInformation : Endpoints {
    @Effective("api/geo")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "geo"

    @Effective("api/geo/geocode")
    val GeoCode: UrlPattern.Relative0 = baseUrl + "geocode"
    fun geoCodeUrl(): Url.Relative = GeoCode.resolve()

    @Effective("api/geo/distance")
    val GeoRoute: UrlPattern.Relative0 = baseUrl + "distance"
    fun geoRouteUrl(): Url.Relative = GeoRoute.resolve()

    const val OpenSourceRoutingMachine: String = "router.project-osrm.org"
    fun openSourceRoutingMachineUrl(): String = OpenSourceRoutingMachine

    const val OpenStreetMap: String = "nominatim.openstreetmap.org"
    fun openStreetMapUrl(): String = OpenStreetMap
  }

  @Deprecated("Currently not used in production")
  object EuScienceHub : Endpoints {
    @Effective("api/eu-science-hub")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "eu-science-hub"
  }

  /**
   * PDF Report Service
   */
  object PdfReport : PdfEndpoints {
    @Effective("api/pdf")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "pdf"
  }

  /**
   * Report Preview Service (provides HTML view)
   */
  object PdfPreview : PdfEndpoints {
    @Effective("api/pdf-preview")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "pdf-preview"
  }

  /**
   * Comments endpoints
   */
  object Comments : Endpoints {
    @Effective("api/comments")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "comments"

    @Effective("api/comments/all-comments")
    val AllComments: UrlPattern.Relative0 = baseUrl + "all-comments"
    fun allCommentsUrl(): Url.Relative = AllComments.resolve()

    @Effective("api/comments/{uuid}")
    val CommentsForComponent: UrlPattern.Relative1 = baseUrl + EndpointsParameters.Uuid
    fun commentsForComponentUrl(uuid: Uuid): Url.Relative = CommentsForComponent.resolve(uuid)

    val CommentsForComponentExample: Url.Relative = CommentsForComponent.resolve(Uuid.random())
    fun commentsForComponentExampleUrl(): Url.Relative = CommentsForComponentExample

    @Effective("api/comments/duplicate-comments")
    val DuplicateComments: UrlPattern.Relative0 = baseUrl + "duplicate-comments"
    fun duplicateCommentsUrl(): Url.Relative = DuplicateComments.resolve()
  }

  /**
   * Project query endpoints (combines storage and process states)
   */
  object ProjectQuery : StorageEndpoints, ProcessStatesEndpoints {
    @Effective("api/query")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "query"

    @Effective("api/query/static-price-list")
    val StaticPriceList: UrlPattern.Relative0 = baseUrl + "static-price-list"
    fun staticPriceListUrl(): Url.Relative = StaticPriceList.resolve()

    @Effective("api/query/price-list-export/{priceListExportCategory}")
    val PriceListExport: UrlPattern.Relative1 = baseUrl + "price-list-export" + EndpointsParameters.priceListExportCategory
    fun priceListExportUrl(category: PriceListExportCategory): Url.Relative = PriceListExport.resolve(category.urlSlug)

    @Effective("api/query/accounting-project-previews-export")
    val AccountingProjectPreviewsExport: UrlPattern.Relative0 = baseUrl + "accounting-project-previews-export"
    fun accountingProjectPreviewsExportUrl(): Url.Relative = AccountingProjectPreviewsExport.resolve()

    @Effective("api/query/project-previews-export")
    val ProjectPreviewsExport: UrlPattern.Relative0 = baseUrl + "project-previews-export"
    fun projectPreviewsExportUrl(): Url.Relative = ProjectPreviewsExport.resolve()

    @Effective("api/query/projects/search")
    val ProjectsSearch: UrlPattern.Relative0 = projectsUrl + "search"
    fun projectsSearchUrl(): Url.Relative = ProjectsSearch.resolve()

    @Effective("api/query/project-previews")
    val ProjectPreviews: UrlPattern.Relative0 = baseUrl + "project-previews"
    fun projectPreviewsUrl(): Url.Relative = ProjectPreviews.resolve()

    @Effective("api/query/accounting-projects")
    val AccountingProjects: UrlPattern.Relative0 = baseUrl + "accounting-projects"
    fun accountingProjectsUrl(): Url.Relative = AccountingProjects.resolve()

    @Effective("api/query/project-counts")
    val ProjectCounts: UrlPattern.Relative0 = baseUrl + "project-counts"
    fun projectCountsUrl(): Url.Relative = ProjectCounts.resolve()

    @Effective("api/query/accounting-project-previews")
    val AccountingProjectPreviews: UrlPattern.Relative0 = baseUrl + "accounting-project-previews"
    fun accountingProjectPreviewsUrl(): Url.Relative = AccountingProjectPreviews.resolve()

    @Effective("api/query/accounting-project-previews-with-revenues")
    val AccountingProjectPreviewsWithRevenues: UrlPattern.Relative0 = baseUrl + "accounting-project-previews-with-revenues"
    fun accountingProjectPreviewsWithRevenuesUrl(): Url.Relative = AccountingProjectPreviewsWithRevenues.resolve()

    @Effective("api/query/all-accounting-project-previews-as-csv")
    val AllAccountingProjectPreviewsAsCSV: UrlPattern.Relative0 = baseUrl + "all-accounting-project-previews-as-csv"
    fun allAccountingProjectPreviewsAsCSVUrl(): Url.Relative = AllAccountingProjectPreviewsAsCSV.resolve()

    @Effective("api/query/all-project-previews-as-csv")
    val AllProjectPreviewsAsCSV: UrlPattern.Relative0 = baseUrl + "all-project-previews-as-csv"
    fun allProjectPreviewsAsCSVUrl(): Url.Relative = AllProjectPreviewsAsCSV.resolve()

    @Effective("api/query/all-relevant-accounting-project-previews-as-csv")
    val AllRelevantAccountingProjectPreviewsAsCSV: UrlPattern.Relative0 = baseUrl + "all-relevant-accounting-project-previews-as-csv"
    fun allRelevantAccountingProjectPreviewsAsCSVUrl(): Url.Relative = AllRelevantAccountingProjectPreviewsAsCSV.resolve()

    @Effective("api/query/set-invoice-for-accounting-project-previews")
    val SetInvoiceForAccountingProjectPreviews: UrlPattern.Relative0 = baseUrl + "set-invoice-for-accounting-project-previews"
    fun setInvoiceForAccountingProjectPreviewsUrl(): Url.Relative = SetInvoiceForAccountingProjectPreviews.resolve()

    @Effective("api/query/all-accounted-project-previews-as-csv")
    val AllAccountedProjectPreviewsAsCSV: UrlPattern.Relative0 = baseUrl + "all-accounted-project-previews-as-csv"
    fun allAccountedProjectPreviewsAsCSVUrl(): Url.Relative = AllAccountedProjectPreviewsAsCSV.resolve()
  }

  @Deprecated("ProjectQuery should be used instead; Service is not used in production")
  object ProcessStates : ProcessStatesEndpoints {
    @Effective("api/process-states")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "process-states"
  }

  @Deprecated("ProjectQuery should be used instead; Service is not used in production")
  object Storage : StorageEndpoints {
    @Effective("api/storage")
    override val baseUrl: UrlPattern.Relative0 = BasePath + "storage"
  }
}
